import { pointsToSegments } from '@cutr/constants/windows/presets/resizers';

import { ArcTopAreaHeightInputs } from './ArcDimensionInputs';
import {
  AreaHeightLineInput,
  AreaWidthLineInput,
  FrameHeightLineInput,
  FrameWidthLineInput,
} from './DimensionInputs';
import { MIN_SASH_WIDTH_MM } from './dimensions';
import { MIN_SASH_HEIGHT_MM } from './dimensions';
import { useKonva } from './KonvaContext';
import { useWindowEditor } from './WindowEditorContext';

export const BottomInputPanel = () => {
  const { frameWidthMM, frameConstraints, frameX, windowSpec } =
    useWindowEditor();

  const { minWidthMM, maxWidthMM } = frameConstraints;
  const widthConstraints = { min: minWidthMM, max: maxWidthMM };
  const { min, max } = widthConstraints;

  const { renderStyles } = useKonva();

  const { bottomMM: frameLinesBottomMM } =
    renderStyles.frameDimensionLineOffset;

  if (!windowSpec) return null;

  const presetState = windowSpec.meta.presetState;
  const xDividers = presetState.xDividers;

  const widths = pointsToSegments(xDividers, frameWidthMM);
  const widthsCount = widths.length;

  const widthLines = widths.map((width, i) => {
    const sumUpToIndex = widths
      .slice(0, i)
      .reduce((sum, curr) => sum + curr, 0);

    const neighbourIndex = i === widthsCount - 1 ? i - 1 : i + 1;
    const maxWidth = width + widths[neighbourIndex] - MIN_SASH_WIDTH_MM;

    return {
      key: `area-width-${i}-${width}`,
      index: i,
      box: {
        x: frameX + sumUpToIndex,
        y: frameLinesBottomMM,
        width,
        height: frameLinesBottomMM,
      },
      min: MIN_SASH_WIDTH_MM,
      max: maxWidth,
    };
  });

  return (
    <>
      <FrameWidthLineInput
        key={`frame-width-input-${frameWidthMM}`}
        name="width"
        box={{
          x: frameX,
          y: widthsCount > 1 ? 0 : frameLinesBottomMM,
          width: frameWidthMM,
          height: frameLinesBottomMM,
        }}
        min={min}
        max={max}
      />
      {widthsCount > 1 &&
        widthLines.map((line) => (
          <AreaWidthLineInput
            key={line.key}
            index={line.index}
            box={line.box}
            min={line.min}
            max={line.max}
          />
        ))}
    </>
  );
};

export const LeftInputPanel = () => {
  const { frameHeightMM, frameConstraints, frameY, windowSpec } =
    useWindowEditor();

  const { renderStyles } = useKonva();

  const { minHeightMM, maxHeightMM } = frameConstraints;
  const heightConstraints = { min: minHeightMM, max: maxHeightMM };

  const { min, max } = heightConstraints;
  const { leftMM: frameLinesLeftMM } = renderStyles.frameDimensionLineOffset;
  const { leftMM: windowLinesLeftMM } = renderStyles.areaDimensionLineOffset;

  if (!windowSpec) return null;

  const presetState = windowSpec.meta.presetState;
  const yDividers = presetState.yDividers;

  const heights = pointsToSegments(yDividers, frameHeightMM);
  const heightsCount = heights.length;

  const heightLines = heights.map((height, i) => {
    const sumUpToIndex = heights
      .slice(0, i)
      .reduce((sum, curr) => sum + curr, 0);

    const neighbourIndex = i === heightsCount - 1 ? i - 1 : i + 1;
    const maxHeight = height + heights[neighbourIndex] - MIN_SASH_HEIGHT_MM;

    return {
      key: `area-height-${i}-${height}`,
      index: i,
      box: {
        x: frameLinesLeftMM,
        y: frameY + sumUpToIndex,
        width: windowLinesLeftMM,
        height,
      },
      min: MIN_SASH_HEIGHT_MM,
      max: maxHeight,
    };
  });

  const frameShape = windowSpec.frameShape;
  const hasShapedTop = frameShape.frameType === 'rectangle-arc-top';
  const lastDivider =
    yDividers.length > 0 ? yDividers[yDividers.length - 1] : 0;

  return (
    <>
      <FrameHeightLineInput
        key={`frame-height-input-${frameHeightMM}`}
        name="height"
        box={{
          x: heightsCount > 1 ? 0 : frameLinesLeftMM,
          y: frameY,
          width: frameLinesLeftMM,
          height: frameHeightMM,
        }}
        min={min}
        max={max}
      />
      {heightsCount > 1 &&
        heightLines
          .filter((line) => !(hasShapedTop && line.index === heightsCount - 1))
          .map((line) => (
            <AreaHeightLineInput
              key={line.key}
              index={line.index}
              box={line.box}
              min={line.min}
              max={line.max}
            />
          ))}
      {frameShape.frameType === 'rectangle-arc-top' && (
        <ArcTopAreaHeightInputs
          frameShape={frameShape}
          frameY={frameY}
          areaTopMM={lastDivider}
          areaBelowHeightMM={heights[heightsCount - 2] ?? 0}
        />
      )}
    </>
  );
};
